/**
 * CC2Found — partner found screen.
 * data-screen-label="cc-02 Search · found"
 *
 * Searches for partner on mount (username from flow state).
 * Shows CCAvatar + archetype + Big-3 grid + mutuals badge.
 * "✓ Found" label uses --success color.
 * CTA uses --grad-button, sends CONFIRM.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../../lib/api-client';
import { useAuth } from '../../../store/auth';
import { CCBackdrop, CCAvatar } from './shared';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import type { UseCompatFlowReturn, CompatPartner } from '../../../hooks/useCompatFlow';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// API types
// --------------------------------------------------------------------------

interface SearchUserResponse {
  user: {
    id: string;
    displayName: string;
    username?: string;
    archetype: string;
    big3?: { sun: string; moon: string; rising: string };
    mutualFriendsCount?: number;
  } | null;
}

// Derive a palette from archetype name (deterministic)
function paletteFromArchetype(archetype: string): CompatPartner['palette'] {
  const lower = archetype.toLowerCase();
  if (lower.includes('dark') || lower.includes('academia') || lower.includes('goth')) return 'violet';
  if (lower.includes('rose') || lower.includes('cottage') || lower.includes('fairy')) return 'rose';
  if (lower.includes('cyber') || lower.includes('techno') || lower.includes('futur')) return 'blue';
  return 'gold';
}

function glyphFromArchetype(archetype: string): string {
  const lower = archetype.toLowerCase();
  if (lower.includes('dark') || lower.includes('academia')) return '📚';
  if (lower.includes('witch') || lower.includes('goth')) return '🌙';
  if (lower.includes('cottage') || lower.includes('fairy')) return '🌿';
  if (lower.includes('cyber')) return '⚡';
  return '✦';
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CC2FoundProps {
  flow: UseCompatFlowReturn;
}

export function CC2Found({ flow }: CC2FoundProps): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth((s) => s.jwt);
  const { searchUsername, partner } = flow;

  // Search for the user when this screen mounts (or username changes)
  useEffect(() => {
    if (!searchUsername || !jwt) return;

    async function search() {
      try {
        const res = await apiFetch(
          `/api/v1/users/search?q=${encodeURIComponent(searchUsername)}`,
          jwt ?? '',
        );
        if (res.status === 404 || !res.ok) {
          flow.send({ type: 'NOT_FOUND' });
          return;
        }
        const data = (await res.json()) as SearchUserResponse;
        if (!data.user) {
          flow.send({ type: 'NOT_FOUND' });
          return;
        }
        const u = data.user;
        const found: CompatPartner = {
          id: u.id,
          displayName: u.displayName,
          username: u.username ?? searchUsername,
          archetype: u.archetype,
          big3: u.big3 ?? { sun: '—', moon: '—', rising: '—' },
          mutualFriendsCount: u.mutualFriendsCount ?? 0,
          glyph: glyphFromArchetype(u.archetype),
          palette: paletteFromArchetype(u.archetype),
        };
        flow.send({ type: 'FOUND', partner: found });
      } catch {
        flow.send({ type: 'NOT_FOUND' });
      }
    }

    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUsername, jwt]);

  function handleConfirm() {
    flow.send({ type: 'CONFIRM' });
  }

  // While searching (partner is null but state is 'searching')
  if (!partner) {
    return (
      <div className={styles.root} data-screen-label="cc-02 Search · found">
        <CCBackdrop tone="quiet" />
        <div className={styles.content}>
          <AppHeader title={t('flows:compat.headerTitle')} onBack={() => flow.send({ type: 'RETRY' })} headingId="compat-heading" />
          <div className={styles.computingCenter} style={{ gap: 12 }}>
            <div style={{ fontSize: 32 }}>✦</div>
            <div className={styles.eyebrow}>{t('compat.found.searching')}</div>
          </div>
        </div>
      </div>
    );
  }

  const big3Cells = [
    { sym: '☉', label: t('compat.found.sun'), val: partner.big3.sun },
    { sym: '☾', label: t('compat.found.moon'), val: partner.big3.moon },
    { sym: 'AC', label: t('compat.found.rising'), val: partner.big3.rising },
  ];

  const mutualColors = ['#c89cff', '#90caf9', '#ff8eb0'];

  return (
    <div className={styles.root} data-screen-label="cc-02 Search · found">
      <CCBackdrop tone="quiet" />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('flows:compat.headerTitle')} onBack={() => flow.send({ type: 'RETRY' })} headingId="compat-heading" />

        {/* Found status */}
        <div style={{ marginTop: 16 }}>
          <div className={styles.eyebrowSuccess}>✓ {t('compat.found.foundIn')}</div>

          {/* Partner card */}
          <div className={styles.partnerCard}>
            <CCAvatar glyph={partner.glyph} palette={partner.palette} size={80} />
            <div>
              <div className={styles.partnerHandle}>
                {partner.displayName} · @{partner.username}
              </div>
              <div className={styles.partnerArchetype}>
                <em className={styles.gradAccentCool}>{partner.archetype}</em>
              </div>
            </div>
          </div>
        </div>

        {/* Big 3 grid */}
        <div className={styles.big3Grid}>
          {big3Cells.map((cell, i) => (
            <div key={i} className={styles.big3Cell}>
              <div className={styles.big3Symbol}>{cell.sym}</div>
              <div className={styles.big3Label}>{cell.label}</div>
              <div className={styles.big3Value}>{cell.val}</div>
            </div>
          ))}
        </div>

        {/* Mutuals */}
        {partner.mutualFriendsCount > 0 && (
          <div className={styles.mutualsRow}>
            <div className={styles.mutualsAvatars}>
              {mutualColors.slice(0, Math.min(partner.mutualFriendsCount, 3)).map((c, i) => (
                <div
                  key={i}
                  className={styles.mutualAvatar}
                  style={{
                    background: `radial-gradient(circle at 35% 35%, ${c}, #1a1340)`,
                    marginLeft: i > 0 ? -8 : 0,
                  }}
                />
              ))}
            </div>
            <div className={styles.mutualsText}>
              <strong style={{ color: 'var(--cream)' }}>{partner.mutualFriendsCount === 1 ? t('compat.found.mutualOne', { count: partner.mutualFriendsCount }) : t('compat.found.mutualMany', { count: partner.mutualFriendsCount })}</strong>
              {partner.mutualFriendsCount > 0 ? t('compat.found.mutualsIn') : ''}
            </div>
          </div>
        )}

        {/* No charge note */}
        <div className={styles.noChargeNote}>
          {t('compat.found.noCharge')}
        </div>

        {/* CTA */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleConfirm}
            data-testid="cc2-confirm-btn"
          >
            {t('compat.found.cta')} ✦
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
